import { z } from "zod";
import { withHttps } from "./webAddress";

/** The legal texts wired in (M6 round seven, task 6; brief "The legal pages"; review B11, S11): the four documents and their one
 *  address, versions that are dates compared as dates (0 is the placeholders' version, under which nothing is asked), the front matter
 *  the Markdown files open with, the acceptance's body and what the workspace read says about the terms and the DPA. */
export const LEGAL_DOCS = ["terms", "privacy", "dpa", "imprint"] as const;
export type LegalDoc = (typeof LEGAL_DOCS)[number];
export const legalPath = (doc: LegalDoc | string): string => `/legal/${doc}`;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** "0" (a placeholder) or a real, zero-padded calendar date */
export function isLegalVersion(v: string): boolean {
  if (v === "0") return true;
  const m = DATE.exec(v); if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d;
}
/** negative, zero or positive; "0" below every date; dates as dates; throws on a string that is not a version (a typo in a file fails loudly) */
export function compareLegalVersions(a: string, b: string): number {
  for (const v of [a, b]) if (!isLegalVersion(v)) throw new Error(`not a legal version: ${JSON.stringify(v)}`);
  if (a === b) return 0;
  if (a === "0") return -1;
  if (b === "0") return 1;
  return a < b ? -1 : 1; // YYYY-MM-DD sorts as text
}
/** whether the owner is asked: never while the version in force is 0; a workspace with none is behind any date */
export function legalBehind(accepted: string | null, current: string): boolean {
  if (current === "0") return false;
  if (accepted === null) return true;
  return compareLegalVersions(accepted, current) < 0;
}
export type LegalDocText = { title: string; version: string; objectionDays: number | null; body: string };
/** the file's front matter (`---` lines with `title:`, `version:` and, for the DPA, `objection_days:`) and the Markdown after it */
export function parseLegalDoc(source: string): LegalDocText {
  const text = source.replace(/\r\n/g, "\n");
  const m = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(text);
  if (!m) throw new Error("a legal text opens with a front matter block (--- title / version ---)");
  const meta: Record<string, string> = {};
  for (const line of m[1]!.split("\n")) { const kv = /^([a-z_]+):\s*(.*?)\s*$/.exec(line); if (kv) meta[kv[1]!] = kv[2]!; }
  const version = meta.version ?? "";
  if (!isLegalVersion(version)) throw new Error(`the legal text's version is not 0 or a date: ${JSON.stringify(version)}`);
  const days = meta.objection_days === undefined ? null : Number(meta.objection_days);
  if (days !== null && !(Number.isInteger(days) && days > 0)) throw new Error(`objection_days is not a whole number of days: ${meta.objection_days}`);
  return { title: (meta.title ?? "").trim() || "Untitled", version, objectionDays: days, body: m[2] ?? "" };
}
const Version = z.string().refine(isLegalVersion, "not a legal version");
export const TermsAcceptSchema = z.object({ version: Version }).strict();
export const LegalIndexSchema = z.object({
  terms: z.object({ title: z.string(), version: Version, url: z.string() }),
  privacy: z.object({ title: z.string(), version: Version, url: z.string() }),
  dpa: z.object({ title: z.string(), version: Version, url: z.string() }),
  imprint: z.object({ title: z.string(), version: Version, url: z.string() }),
});
export type LegalIndex = z.infer<typeof LegalIndexSchema>;
/** the workspace's own privacy policy, linked on its public form when set: an https address of at most 500 characters, else nothing */
export const PrivacyUrl = z.string().transform(withHttps).pipe(z.string().max(500)).refine((v) => { if (!/^https:\/\/\S+$/.test(v)) return false; try { return new URL(v).protocol === "https:"; } catch { return false; } }, "an https:// address");
